/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState } from "react";
import { Tabs, Table, Typography, Space, DatePicker, Flex } from "antd";
import { CaretDownOutlined, CaretUpOutlined } from "@ant-design/icons";
import "./App.css";
import dayjs, { Dayjs } from "dayjs";
import { ColumnsType } from "antd/es/table";
import { useS3Data } from "./hooks/useS3Data";
import { Stock, StockData } from "./types";

export const getClosestPastWeekday = (date: Dayjs) => {
  const dayOfWeek = date.day(); // 0: Sunday, 1: Monday, ..., 6: Saturday

  if (dayOfWeek === 0) {
    // Sunday -> move to Friday
    return date.subtract(2, "day");
  } else if (dayOfWeek === 6) {
    // Saturday -> move to Friday
    return date.subtract(1, "day");
  }

  // Otherwise, it's already a weekday
  return date;
};

const StockReport = () => {
  const [date, setDate] = useState(getClosestPastWeekday(dayjs()));
  const [activeKey, setActiveKey] = useState("low_up");

  const dateString = date ? date.format("YYYY-MM-DD") : null;
  const s3Key = dateString ? `data/stock/${activeKey}-${dateString}.json` : '';
  const { data: stocks = {}, isLoading: loading } = useS3Data<StockData>(s3Key, !!dateString);

  const columns = [
    {
      title: "등락률",
      dataIndex: "percent",
      key: "percent",
      width: 90,
      render: (text) => {
        return (
          <>
            {activeKey === "low_up" ? (
              <CaretUpOutlined
                style={{
                  color: "red",
                }}
              />
            ) : (
              <CaretDownOutlined
                style={{
                  color: "blue",
                }}
              />
            )}
            <Typography.Text
              style={
                activeKey === "low_up" ? { color: "red" } : { color: "blue" }
              }
            >
              {`${activeKey === "low_up" ? "+" : "-"}${text}%`}
            </Typography.Text>
          </>
        );
      },
    },
    {
      title: "종목명",
      dataIndex: "itemName",
      key: "itemName",
      width: 150,
    },
    {
      title: "현재가",
      dataIndex: "price",
      key: "price",
      render: (text) => {
        return <Typography.Text>{text.toLocaleString()}</Typography.Text>;
      },
      width: 80,
      sorter: (a, b) => a.price - b.price,
    },

    {
      title: "시가",
      dataIndex: "startPrice",
      key: "startPrice",
      render: (text) => {
        return <Typography.Text>{text.toLocaleString()}</Typography.Text>;
      },
      width: 80,
      sorter: (a, b) => a.startPrice - b.startPrice,
    },
    {
      title: "고가",
      dataIndex: "highPrice",
      key: "highPrice",
      render: (text) => {
        return <Typography.Text>{text.toLocaleString()}</Typography.Text>;
      },
      width: 80,
      sorter: (a, b) => a.highPrice - b.highPrice,
    },
    {
      title: "저가",
      dataIndex: "lowPrice",
      key: "lowPrice",
      render: (text) => {
        return <Typography.Text>{text.toLocaleString()}</Typography.Text>;
      },
      width: 80,
      sorter: (a, b) => a.lowPrice - b.lowPrice,
    },
    {
      title: "거래량",
      dataIndex: "volume",
      key: "volume",
      render: (text) => {
        return <Typography.Text>{text.toLocaleString()}</Typography.Text>;
      },
      width: 80,
      sorter: (a, b) => a.volume - b.volume,
    },
    // {
    //   title: "PER",
    //   dataIndex: "per",
    //   key: "per",
    //   width: 60,
    // },
    // {
    //   title: "ROE",
    //   dataIndex: "roe",
    //   key: "roe",
    //   width: 60,
    // },
  ] as ColumnsType<{
    price: number;
    startPrice: number;
    highPrice: number;
    lowPrice: number;
    volume: number;
    percent: number;
    itemName: string;
  }>;

  const filterAndExtractPercent = (data: Stock[], isLowUp?: boolean) => {
    return data
      .filter((stock) => {
        const percentValue = stock.percent.trim();
        return isLowUp
          ? percentValue.startsWith("-")
          : percentValue.startsWith("+");
      })
      .map((stock) => ({
        ...stock,
        price: Number(stock.price.replace(/[^0-9]/g, "")),
        startPrice: Number(stock.startPrice.replace(/[^0-9]/g, "")),
        highPrice: Number(stock.highPrice.replace(/[^0-9]/g, "")),
        lowPrice: Number(stock.lowPrice.replace(/[^0-9]/g, "")),
        volume: Number(stock.volume.replace(/[^0-9]/g, "")),
        percent: Number(stock.percent.replace(/[^0-9.]/g, "")),
      }))
      .sort((a, b) => {
        return b.percent - a.percent;
      });
  };

  const renderTable = (data: Stock[], isLowUp?: boolean) => {
    const filteredData = filterAndExtractPercent(data, isLowUp);
    return (
      <Table
        size="small"
        dataSource={filteredData}
        columns={columns}
        rowKey="itemName"
        pagination={false}
        scroll={{ y: "80vh" }}
        loading={loading}
      />
    );
  };

  return (
    <div>
      <Flex
        style={{
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            flexDirection: "column",
            gap: 16,
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 16,
              alignItems: "center",
            }}
          >
            <Typography.Title
              level={5}
              style={{
                marginBottom: 0,
              }}
            >
              급등주 / 급락주
            </Typography.Title>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 16,
              }}
            >
              <Typography.Text
                style={{
                  // description
                  color: "gray",
                }}
              >
                8.30 이후부터 데이터 조회가능
              </Typography.Text>
            </div>
          </div>
          <DatePicker value={date} onChange={(e) => setDate(e)} />
        </div>
      </Flex>
      <Tabs
        activeKey={activeKey}
        onChange={(key) => setActiveKey(key)}
        centered
      >
        <Tabs.TabPane tab="급등주" key="low_up">
          <Flex
            gap={20}
            style={{
              width: "100%",
            }}
          >
            <Flex
              style={{
                flexDirection: "column",
                width: "50%",
              }}
            >
              <Typography.Title level={5}>코스피</Typography.Title>
              {renderTable(stocks?.kospiStocks || [])}
            </Flex>
            <Flex
              style={{
                flexDirection: "column",
                width: "48%",
              }}
            >
              <Typography.Title level={5}>코스닥</Typography.Title>
              {renderTable(stocks?.kosdockStocks || [])}
            </Flex>
          </Flex>
        </Tabs.TabPane>
        <Tabs.TabPane tab="급락주" key="high_down">
          <Flex
            gap={20}
            style={{
              width: "100%",
            }}
          >
            <Flex
              style={{
                flexDirection: "column",
                width: "48%",
              }}
            >
              <Typography.Title level={5}>코스피</Typography.Title>
              {renderTable(stocks?.kospiStocks || [], true)}
            </Flex>
            <Flex
              style={{
                flexDirection: "column",
                width: "50%",
              }}
            >
              <Typography.Title level={5}>코스닥</Typography.Title>
              {renderTable(stocks?.kosdockStocks || [], true)}
            </Flex>
          </Flex>
        </Tabs.TabPane>
      </Tabs>
    </div>
  );
};

export default StockReport;
